import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import {
  getTripPreviewHotelByCardKey,
  TripPreviewFlightCard,
  TripPreviewHotelCard,
  TripPreviewSection,
  TripPreviewSlotView,
} from './tripResultBuilderPreview';

export type TripBuilderRouteStopKind = 'start-flight' | 'hotel' | 'middle-flight' | 'end-flight';

export type TripBuilderRouteStop = {
  label: string;
  title: string;
  lat?: number;
  lng?: number;
  kind: TripBuilderRouteStopKind;
};

export const TRIP_BUILDER_ROUTE_COLORS = {
  default: '#9b4d4d',
  startFlight: '#107ab0',
  endFlight: '#4f8a62',
  middleFlight: '#9a6b45',
} as const;

const STATIC_MARKER_COLORS: Record<TripBuilderRouteStopKind, string> = {
  'start-flight': '0x107ab0',
  hotel: '0x9b4d4d',
  'middle-flight': '0x9a6b45',
  'end-flight': '0x4f8a62',
};

export const normalizeRouteCity = (value: string) =>
  value.toLowerCase().trim().replace(/\s+/g, ' ');

const parseCoords = (lat?: string | number | null, lng?: string | number | null) => {
  if (lat == null || lng == null) {
    return null;
  }
  const parsedLat = Number(lat);
  const parsedLng = Number(lng);
  if (Number.isNaN(parsedLat) || Number.isNaN(parsedLng)) {
    return null;
  }
  return { lat: parsedLat, lng: parsedLng };
};

const getHotelCoords = (hotel: TripPreviewHotelCard, proposal: ProposalData) => {
  const serpCoords = parseCoords(
    hotel.serpHotel?.gpsCoordinates?.latitude,
    hotel.serpHotel?.gpsCoordinates?.longitude,
  );
  if (serpCoords) {
    return serpCoords;
  }

  const tripAdvisorCoords = parseCoords(
    hotel.tripAdvisorHotel?.latitude,
    hotel.tripAdvisorHotel?.longitude,
  );
  if (tripAdvisorCoords) {
    return tripAdvisorCoords;
  }

  const proposalHotel = getTripPreviewHotelByCardKey(proposal, hotel.key);
  return parseCoords(proposalHotel?.geoLocation?.latitude, proposalHotel?.geoLocation?.longitude);
};

const getActiveHotelCoords = (section: TripPreviewSection, proposal: ProposalData) => {
  const activeHotel = section.hotels.find((hotel) => hotel.isActive);
  if (!activeHotel) {
    return null;
  }
  return getHotelCoords(activeHotel, proposal);
};

const geocodeWithGoogle = (query: string, language?: string) =>
  new Promise<{ lat: number; lng: number } | null>((resolve) => {
    if (!window.google?.maps?.Geocoder) {
      resolve(null);
      return;
    }

    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: query, language: language || 'en' }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK || !results?.[0]?.geometry?.location) {
        resolve(null);
        return;
      }

      const location = results[0].geometry.location;
      resolve({ lat: location.lat(), lng: location.lng() });
    });
  });

const geocodeWithPhoton = async (query: string) => {
  const response = await fetch(
    `https://photon.komoot.io/api/?q=${encodeURIComponent(query)}&limit=1`,
  );
  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  const coordinates = data?.features?.[0]?.geometry?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }

  return parseCoords(coordinates[1], coordinates[0]);
};

export const geocodeRouteLabel = async (label: string, language?: string) => {
  const queries = [label];
  if (/^[A-Za-z]{3}$/.test(label.trim())) {
    queries.push(`${label} airport`);
  }

  for (const query of queries) {
    const googleCoords = await geocodeWithGoogle(query, language);
    if (googleCoords) {
      return googleCoords;
    }

    const photonCoords = await geocodeWithPhoton(query);
    if (photonCoords) {
      return photonCoords;
    }
  }

  return null;
};

export type TripBuilderProposalHotelMarker = {
  key: string;
  name: string;
  city: string;
  isActive: boolean;
  lat?: number;
  lng?: number;
};

export const buildTripBuilderProposalHotels = (
  slots: TripPreviewSlotView[],
  proposal: ProposalData,
): TripBuilderProposalHotelMarker[] => {
  const hotels: TripBuilderProposalHotelMarker[] = [];
  const seen = new Set<string>();

  slots.forEach((slot) => {
    slot.chainNodes.forEach((node) => {
      node.hotelSection?.hotels.forEach((hotel) => {
        if (seen.has(hotel.key)) {
          return;
        }
        seen.add(hotel.key);
        const coords = getHotelCoords(hotel, proposal);
        hotels.push({
          key: hotel.key,
          name: hotel.name,
          city: node.city,
          isActive: hotel.isActive,
          lat: coords?.lat,
          lng: coords?.lng,
        });
      });
    });
  });

  return hotels;
};

export const buildTripBuilderRouteStops = (
  slots: TripPreviewSlotView[],
  proposal: ProposalData,
): TripBuilderRouteStop[] => {
  const stops: TripBuilderRouteStop[] = [];
  const cityCoords = new Map<string, { lat: number; lng: number }>();

  slots.forEach((slot) => {
    slot.chainNodes.forEach((node) => {
      if (!node.hotelSection) {
        return;
      }
      const coords = getActiveHotelCoords(node.hotelSection, proposal);
      if (coords && node.city) {
        cityCoords.set(normalizeRouteCity(node.city), coords);
      }
    });
  });

  const resolveCoords = (label: string, sectionCoords?: { lat: number; lng: number } | null) => {
    if (sectionCoords) {
      return sectionCoords;
    }
    return cityCoords.get(normalizeRouteCity(label)) || null;
  };

  const pushStop = (
    label?: string,
    sectionCoords?: { lat: number; lng: number } | null,
    kind: TripBuilderRouteStopKind = 'hotel',
    title?: string,
  ) => {
    if (!label) {
      return;
    }
    const normalized = normalizeRouteCity(label) || label.trim().toLowerCase();
    if (!normalized) {
      return;
    }
    const last = stops[stops.length - 1];
    if (
      last &&
      (normalizeRouteCity(last.label) || last.label.trim().toLowerCase()) === normalized
    ) {
      const skipDuplicateHotel = kind === 'hotel' && last.kind === 'hotel';
      const skipHotelAtFlightEndpoint =
        kind === 'hotel' &&
        (last.kind === 'start-flight' ||
          last.kind === 'middle-flight' ||
          last.kind === 'end-flight');
      if (skipDuplicateHotel || skipHotelAtFlightEndpoint) {
        return;
      }
    }
    const coords = resolveCoords(label, sectionCoords);
    stops.push({
      label,
      title: title || label,
      lat: coords?.lat,
      lng: coords?.lng,
      kind,
    });
  };

  const getFlightEndpoints = (flight: TripPreviewFlightCard) => {
    const [fromCode, toCode] = flight.route.split('->').map((part) => part.trim());
    return {
      departure: flight.departureLabel || fromCode,
      arrival: flight.arrivalLabel || toCode,
    };
  };

  const pushFlightEndpoints = (
    flight: TripPreviewFlightCard | undefined,
    kind: TripBuilderRouteStopKind,
  ) => {
    if (!flight?.isActive) {
      return;
    }
    const { departure, arrival } = getFlightEndpoints(flight);
    pushStop(departure, null, kind, departure);
    pushStop(arrival, null, kind, arrival);
  };

  slots.forEach((slot) => {
    if (slot.beforeSection) {
      pushFlightEndpoints(
        slot.beforeSection.flights.find((card) => card.isActive),
        'start-flight',
      );
    }

    slot.chainNodes.forEach((node) => {
      const activeHotel = node.hotelSection?.hotels.find((hotel) => hotel.isActive);
      if (activeHotel) {
        const coords = getActiveHotelCoords(node.hotelSection!, proposal);
        pushStop(node.city, coords, 'hotel', activeHotel.name);
      }

      node.middleSections.forEach((section) => {
        pushFlightEndpoints(
          section.flights.find((card) => card.isActive),
          'middle-flight',
        );
      });
    });

    if (slot.afterSection) {
      pushFlightEndpoints(
        slot.afterSection.flights.find((card) => card.isActive),
        'end-flight',
      );
    }
  });

  return stops;
};

export const buildTripBuilderRouteDisplayLabels = (
  slots: TripPreviewSlotView[],
  proposal: ProposalData,
): string[] => {
  const routeStops = buildTripBuilderRouteStops(slots, proposal);
  if (routeStops.length) {
    return routeStops.map((stop) => stop.label);
  }

  const labels: string[] = [];
  const seen = new Set<string>();

  const addLabel = (label?: string) => {
    if (!label?.trim()) {
      return;
    }
    const key = normalizeRouteCity(label) || label.trim().toLowerCase();
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    labels.push(label.trim());
  };

  slots.forEach((slot) => {
    slot.chainNodes.forEach((node) => addLabel(node.city));
  });

  return labels;
};

export const resolveTripBuilderRouteStops = (
  stops: TripBuilderRouteStop[],
  geocodedCoords: Record<string, { lat: number; lng: number }>,
) =>
  stops.map((stop) => {
    if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
      return stop;
    }
    const geocoded = geocodedCoords[normalizeRouteCity(stop.label)];
    if (!geocoded) {
      return stop;
    }
    return {
      ...stop,
      lat: geocoded.lat,
      lng: geocoded.lng,
    };
  });

export const buildTripBuilderGoogleMapsUrl = (stops: TripBuilderRouteStop[], language?: string) => {
  const getStopPoint = (stop: TripBuilderRouteStop) => {
    if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
      return `${stop.lat},${stop.lng}`;
    }
    return encodeURIComponent(stop.label);
  };

  const points = stops.map(getStopPoint).filter(Boolean);

  if (!points.length) {
    return '';
  }

  if (points.length === 1) {
    const stop = stops[0];
    if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
      return appendGoogleMapsLanguage(
        `https://www.google.com/maps/search/?api=1&query=${stop.lat},${stop.lng}`,
        language,
      );
    }
    return appendGoogleMapsLanguage(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(stop.label)}`,
      language,
    );
  }

  return appendGoogleMapsLanguage(`https://www.google.com/maps/dir/${points.join('/')}`, language);
};

export const getTripBuilderGoogleMapsApiKey = () => {
  try {
    const raw =
      sessionStorage.getItem('initSiteData') || localStorage.getItem('initSiteData') || '{}';
    return JSON.parse(raw).apiKey || '';
  } catch {
    return '';
  }
};

export const getTripBuilderGoogleMapsRegion = (language: string) =>
  language === 'he' ? 'IL' : language;

export const appendGoogleMapsLanguage = (url: string, language?: string) => {
  if (!language) {
    return url;
  }
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}hl=${encodeURIComponent(language)}`;
};

export const buildTripBuilderHotelsGoogleMapsUrl = (
  hotels: TripBuilderProposalHotelMarker[],
  language?: string,
) => {
  const getHotelPoint = (hotel: TripBuilderProposalHotelMarker) => {
    if (typeof hotel.lat === 'number' && typeof hotel.lng === 'number') {
      return `${hotel.lat},${hotel.lng}`;
    }
    return encodeURIComponent(`${hotel.name}, ${hotel.city}`);
  };

  const points = hotels.map(getHotelPoint).filter(Boolean);

  if (!points.length) {
    return appendGoogleMapsLanguage('https://www.google.com/maps', language);
  }

  if (points.length === 1) {
    const hotel = hotels[0];
    if (typeof hotel.lat === 'number' && typeof hotel.lng === 'number') {
      return appendGoogleMapsLanguage(
        `https://www.google.com/maps/search/?api=1&query=${hotel.lat},${hotel.lng}`,
        language,
      );
    }
    return appendGoogleMapsLanguage(
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${hotel.name}, ${hotel.city}`)}`,
      language,
    );
  }

  return appendGoogleMapsLanguage(`https://www.google.com/maps/dir/${points.join('/')}`, language);
};

const getStopLocationParam = (stop: TripBuilderRouteStop) => {
  if (typeof stop.lat === 'number' && typeof stop.lng === 'number') {
    return `${stop.lat},${stop.lng}`;
  }
  return encodeURIComponent(stop.label);
};

const getGoogleStaticMapSizeQuery = (width: number, height: number) => {
  const maxDim = 640;
  let sizeW = Math.round(width);
  let sizeH = Math.round(height);
  if (sizeW > maxDim || sizeH > maxDim) {
    const ratio = maxDim / Math.max(sizeW, sizeH);
    sizeW = Math.round(sizeW * ratio);
    sizeH = Math.round(sizeH * ratio);
  }
  return `size=${sizeW}x${sizeH}`;
};

export const loadStaticMapImageSrc = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.referrerPolicy = 'no-referrer';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(url);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(url);
      }
    };
    img.onerror = () => reject(new Error('Failed to load map image'));
    img.src = url;
  });

export const buildTripBuilderStaticMapImageUrl = (
  stops: TripBuilderRouteStop[],
  options?: { apiKey?: string; width?: number; height?: number },
) => {
  if (!stops.length) {
    return '';
  }

  const width = options?.width ?? 800;
  const height = options?.height ?? 340;
  const apiKey = options?.apiKey;

  if (apiKey) {
    const markers = stops
      .map((stop, index) => {
        const color = STATIC_MARKER_COLORS[stop.kind];
        const location = getStopLocationParam(stop);
        const label = String(Math.min(index + 1, 9));
        return `markers=color:${color}%7Clabel:${label}%7C${location}`;
      })
      .join('&');

    const pathStops = stops.filter(
      (stop): stop is TripBuilderRouteStop & { lat: number; lng: number } =>
        typeof stop.lat === 'number' && typeof stop.lng === 'number',
    );
    const path =
      pathStops.length > 1
        ? `&path=color:0x9b4d4d%7Cweight:2%7C${pathStops.map((stop) => `${stop.lat},${stop.lng}`).join('|')}`
        : '';

    return `https://maps.googleapis.com/maps/api/staticmap?key=${apiKey}&${getGoogleStaticMapSizeQuery(width, height)}&${markers}${path}`;
  }

  const pathStops = stops.filter(
    (stop): stop is TripBuilderRouteStop & { lat: number; lng: number } =>
      typeof stop.lat === 'number' && typeof stop.lng === 'number',
  );
  if (!pathStops.length) {
    return '';
  }

  const centerLat = pathStops.reduce((sum, stop) => sum + stop.lat, 0) / pathStops.length;
  const centerLng = pathStops.reduce((sum, stop) => sum + stop.lng, 0) / pathStops.length;
  const markers = pathStops.map((stop) => `${stop.lat},${stop.lng},red-pushpin`).join('|');

  return `https://staticmap.openstreetmap.de/staticmap.php?center=${centerLat},${centerLng}&zoom=5&size=${width}x${height}&markers=${markers}`;
};

export const getTripBuilderRouteSegmentColor = (
  from: TripBuilderRouteStop,
  to: TripBuilderRouteStop,
) => {
  if (from.kind === 'start-flight' && to.kind === 'start-flight') {
    return TRIP_BUILDER_ROUTE_COLORS.startFlight;
  }
  if (from.kind === 'end-flight' && to.kind === 'end-flight') {
    return TRIP_BUILDER_ROUTE_COLORS.endFlight;
  }
  if (from.kind === 'middle-flight' && to.kind === 'middle-flight') {
    return TRIP_BUILDER_ROUTE_COLORS.middleFlight;
  }
  if (to.kind === 'end-flight') {
    return TRIP_BUILDER_ROUTE_COLORS.endFlight;
  }
  return TRIP_BUILDER_ROUTE_COLORS.default;
};

export const getTripBuilderRouteStopTypeLabel = (kind: TripBuilderRouteStopKind) =>
  kind === 'hotel' ? 'Hotel' : 'Airport';
